import { _config, mainApiKey } from '../config'
import { encrypt } from '../utils/encrypt'

const API_BASE_URL = _config.backendUrl
console.log(API_BASE_URL)

class ApiService {
	async request(endpoint, options = {}) {
		const url = `${API_BASE_URL}${endpoint}?api_token=${mainApiKey}`

		console.log(`Making API request to: ${url}`)

		const config = {
			headers: {
				accept: 'application/json',
				'Content-Type': 'application/json',
				...options.headers,
			},
			...options,
		}

		if (options.body) {
			config.body = JSON.stringify(options.body)
		}

		try {
			const response = await fetch(url, config)

			console.log(`Response status: ${response.status} for ${endpoint}`)

			if (!response.ok) {
				const errorText = await response.text()
				console.error(`API Error: ${errorText}`)
				throw new Error(
					`HTTP error! status: ${response.status}, message: ${errorText}`,
				)
			}

			const data = await response.json()
			console.log(`API Response for ${endpoint}:`, data)
			return data
		} catch (error) {
			console.error('API request failed:', error)
			throw error
		}
	}

	// ========== AUTH API ==========
	async authUser(login, password) {
		const data = await this.request('/auth', {
			method: 'POST',
			body: { login, password },
		})
		return data
	}

	// ========== CONNECTIONS API ==========

	// Преобразуем наши данные в формат API
	transformConnectionData(formData) {
		return {
			user_id: 1,
			dbms_type: formData.db_type,
			connection_name: formData.name,
			description:
				formData.description || `Connection to ${formData.db_type} database`,
			host: formData.host,
			port: parseInt(formData.port) || null,
			db_name: formData.database_name,
			username: formData.username,
			password: encrypt(formData.password),
		}
	}

	// Обратное преобразование данных из API
	transformApiConnection(apiData) {
		return {
			id: apiData.connection_id,
			name: apiData.connection_name,
			db_type: apiData.dbms_type,
			host: apiData.host,
			port: apiData.port?.toString() || '',
			database_name: apiData.db_name,
			username: apiData.username,
			description: apiData.description,
		}
	}

	// Connections API
	async getConnections() {
		const data = await this.request('/connections')
		return Array.isArray(data)
			? data.map(conn => this.transformApiConnection(conn))
			: []
	}

	async getConnection(id) {
		const data = await this.request(`/connections/${id}`)
		return this.transformApiConnection(data)
	}

	async createConnection(connectionData) {
		const apiData = this.transformConnectionData(connectionData)
		const data = await this.request('/connections', {
			method: 'POST',
			body: apiData,
		})
		return this.transformApiConnection(data)
	}

	async updateConnection(id, connectionData) {
		const apiData = this.transformConnectionData(connectionData)
		const data = await this.request(`/connections/${id}`, {
			method: 'PATCH',
			body: apiData,
		})
		return this.transformApiConnection(data)
	}

	async deleteConnection(id) {
		return this.request(`/connections/${id}`, {
			method: 'DELETE',
		})
	}

	// ========== CHAINS API (это и есть очереди) ==========

	// Преобразуем данные цепочки в формат API
	transformChainData(formData) {
		return {
			user_id: 1,
			connection_id: parseInt(formData.connectionId) || 1,
			chain_name: formData.name,
			description: formData.description || `Chain for datamart`,
			upload_result: formData.systemResult || true,
		}
	}

	// Обратное преобразование данных цепочки из API
	transformApiChain(apiData) {
		console.log('Transforming chain data from API:', apiData)

		// Проверяем разные возможные названия полей из API
		const id = apiData.id || apiData.chain_id || apiData.ID
		const name =
			apiData.chain_name || apiData.name || apiData.queue_name || 'Без названия'
		const connectionId = apiData.connection_id || apiData.connectionId
		const description = apiData.description || ''
		const systemResult =
			apiData.upload_result !== undefined ? apiData.upload_result : true
		const status = apiData.status || 'stopped'

		return {
			id: id,
			name: name,
			connectionId: connectionId,
			description: description,
			systemResult: systemResult,
			status: status,
		}
	}

	// Получить все цепочки
	async getChains() {
		try {
			console.log('Fetching chains...')
			const data = await this.request('/chains')

			console.log('Chains API response:', data)

			// Обрабатываем разные форматы ответа
			let chainsArray
			if (Array.isArray(data)) {
				chainsArray = data
			} else if (data && data.chains) {
				chainsArray = data.chains
			} else if (data && data.data) {
				chainsArray = data.data
			} else {
				chainsArray = []
			}

			console.log('Processed chains array:', chainsArray)
			return chainsArray.map(chain => this.transformApiChain(chain))
		} catch (error) {
			console.error('Error in getChains:', error)
			throw error
		}
	}

	// Получить цепочку по ID
	async getChain(id) {
		// Проверяем и преобразуем ID
		const numericId = parseInt(id)
		if (isNaN(numericId)) {
			console.error('Invalid chain ID received:', id)
			throw new Error(`Invalid chain ID: ${id}`)
		}

		console.log(`Fetching chain with ID: ${numericId}`)
		const data = await this.request(`/chains/${numericId}`)
		return this.transformApiChain(data)
	}

	// Создать цепочку
	async createChain(chainData) {
		const apiData = this.transformChainData(chainData)
		console.log('Creating chain with data:', apiData)

		const data = await this.request('/chains', {
			method: 'POST',
			body: apiData,
		})
		return this.transformApiChain(data)
	}

	// Обновить цепочку
	async updateChain(id, chainData) {
		const apiData = this.transformChainData(chainData)
		const data = await this.request(`/chains/${id}`, {
			method: 'PATCH',
			body: apiData,
		})
		return this.transformApiChain(data)
	}

	// Удалить цепочку
	async deleteChain(id) {
		return this.request(`/chains/${id}`, {
			method: 'DELETE',
		})
	}

	// Запустить цепочку
	async runChain(id) {
		return this.request(`/chains/${id}/run`, {
			method: 'POST',
		})
	}

	// ========== QUERIES API ==========

	// Преобразуем данные запроса в формат API
	transformQueryData(formData, chainId) {
		return {
			connection_id: parseInt(formData.connectionId) || 1,
			query_name: formData.name,
			table_name: formData.tableName,
			step_order: parseInt(formData.position) || 1,
			query_text: formData.sql,
			description: formData.description || '',
		}
	}

	// Обратное преобразование данных запроса из API
	transformApiQuery(apiData) {
		return {
			id: apiData.query_id,
			name: apiData.query_name,
			sql: apiData.query_text,
			tableName: apiData.table_name,
			position: apiData.step_order,
			description: apiData.description,
			connectionId: apiData.connection_id,
			chainId: apiData.chain_id,
		}
	}

	// Получить все запросы цепочки
	async getChainQueries(chainId) {
		try {
			// Проверяем ID
			const numericChainId = parseInt(chainId)
			if (isNaN(numericChainId)) {
				console.error('Invalid chainId for queries:', chainId)
				return []
			}

			// Если нет отдельного эндпоинта для запросов цепочки,
			// получаем все запросы и фильтруем по chain_id
			const data = await this.request('/queries')

			let queriesArray
			if (Array.isArray(data)) {
				queriesArray = data.filter(query => {
					const queryChainId = parseInt(query.chain_id)
					return !isNaN(queryChainId) && queryChainId === numericChainId
				})
			} else if (data && data.queries) {
				queriesArray = data.queries.filter(query => {
					const queryChainId = parseInt(query.chain_id)
					return !isNaN(queryChainId) && queryChainId === numericChainId
				})
			} else if (data && data.data) {
				queriesArray = data.data.filter(query => {
					const queryChainId = parseInt(query.chain_id)
					return !isNaN(queryChainId) && queryChainId === numericChainId
				})
			} else {
				queriesArray = []
			}

			console.log(
				`Found ${queriesArray.length} queries for chain ${numericChainId}`,
			)
			return queriesArray.map(query => this.transformApiQuery(query))
		} catch (error) {
			console.error('Error in getChainQueries:', error)
			return [] // Возвращаем пустой массив вместо ошибки
		}
	}

	// Получить запрос по ID
	async getQuery(queryId) {
		const data = await this.request(`/queries/${queryId}`)
		return this.transformApiQuery(data)
	}

	// Создать запрос
	async createQuery(queryData, chainId) {
		const apiData = this.transformQueryData(queryData, chainId)
		// Добавляем chain_id в данные запроса
		apiData.chain_id = parseInt(chainId)

		const data = await this.request('/queries', {
			method: 'POST',
			body: apiData,
		})
		return this.transformApiQuery(data)
	}

	// Обновить запрос
	async updateQuery(queryId, queryData, chainId) {
		const apiData = this.transformQueryData(queryData, chainId)
		apiData.chain_id = parseInt(chainId)

		const data = await this.request(`/queries/${queryId}`, {
			method: 'PATCH',
			body: apiData,
		})
		return this.transformApiQuery(data)
	}

	// Удалить запрос
	async deleteQuery(queryId) {
		return this.request(`/queries/${queryId}`, {
			method: 'DELETE',
		})
	}

	// ========== SCHEDULES API ==========

	// Преобразуем данные расписания в формат API
	transformScheduleData(formData, chainId) {
		return {
			chain_id: parseInt(chainId),
			interval_type: formData.intervalType || 'hour',
			interval: parseInt(formData.intervalValue) || 1,
			enabled: formData.pushNow || true,
			start_at: formData.startAt || new Date().toISOString(),
		}
	}

	// Обратное преобразование данных расписания из API
	transformApiSchedule(apiData) {
		return {
			id: apiData.schedule_id,
			chainId: apiData.chain_id,
			intervalType: apiData.interval_type,
			intervalValue: apiData.interval,
			pushNow: apiData.enabled,
			startAt: apiData.start_at,
			createdAt: apiData.created_at,
			updatedAt: apiData.updated_at,
		}
	}

	// Создать расписание
	async createSchedule(scheduleData, chainId) {
		const apiData = this.transformScheduleData(scheduleData, chainId)

		const data = await this.request('/schedules', {
			method: 'POST',
			body: apiData,
		})
		return this.transformApiSchedule(data)
	}

	// Получить расписания цепочки
	async getChainSchedules(chainId) {
		try {
			const numericChainId = parseInt(chainId)
			if (isNaN(numericChainId)) {
				console.error('Invalid chainId for schedules:', chainId)
				return []
			}

			// Если нет отдельного эндпоинта для расписаний цепочки,
			// получаем все расписания и фильтруем по chain_id
			const data = await this.request('/schedules')

			let schedulesArray
			if (Array.isArray(data)) {
				schedulesArray = data.filter(schedule => {
					const scheduleChainId = parseInt(schedule.chain_id)
					return !isNaN(scheduleChainId) && scheduleChainId === numericChainId
				})
			} else if (data && data.schedules) {
				schedulesArray = data.schedules.filter(schedule => {
					const scheduleChainId = parseInt(schedule.chain_id)
					return !isNaN(scheduleChainId) && scheduleChainId === numericChainId
				})
			} else if (data && data.data) {
				schedulesArray = data.data.filter(schedule => {
					const scheduleChainId = parseInt(schedule.chain_id)
					return !isNaN(scheduleChainId) && scheduleChainId === numericChainId
				})
			} else {
				schedulesArray = []
			}

			return schedulesArray.map(schedule => this.transformApiSchedule(schedule))
		} catch (error) {
			console.error('Error in getChainSchedules:', error)
			return []
		}
	}

	// Удалить расписание
	async deleteSchedule(scheduleId) {
		return this.request(`/schedules/${scheduleId}`, {
			method: 'DELETE',
		})
	}

	// Получить все расписания
	async getSchedules() {
		try {
			const data = await this.request('/schedules')

			let schedulesArray
			if (Array.isArray(data)) {
				schedulesArray = data
			} else if (data && data.schedules) {
				schedulesArray = data.schedules
			} else if (data && data.data) {
				schedulesArray = data.data
			} else {
				schedulesArray = []
			}

			return schedulesArray.map(schedule => this.transformApiSchedule(schedule))
		} catch (error) {
			console.error('Error in getSchedules:', error)
			return []
		}
	}
}

export const apiService = new ApiService()
